import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  queryAi,
  queryAiStream,
  getAiPreferences,
  updateAiPreferences,
  checkAiStatus,
  getAiDailyUsage,
} from "../../services/ai/aiApi";

export const sendMessage = createAsyncThunk(
  "ai/sendMessage",
  async ({ message, conversationId }, { rejectWithValue }) => {
    try {
      const response = await queryAi(message, conversationId);
      return {
        userMessage: message,
        reply: response.reply,
        conversationId: response.conversationId,
        dailyUsed: response.dailyUsed,
        dailyMax: response.dailyMax,
      };
    } catch (err) {
      const data = err?.response?.data || {};
      const errorMessage =
        data.userMessage ||
        err?.message ||
        "Failed to get response";
      return rejectWithValue({
        message: errorMessage,
        retryAfter: data.retryAfter || 0,
        errorCode: data.errorCode || "",
        dailyUsed: data.dailyUsed,
        dailyMax: data.dailyMax,
      });
    }
  },
);

export const sendMessageStream = createAsyncThunk(
  "ai/sendMessageStream",
  async ({ message, conversationId }, { dispatch, rejectWithValue }) => {
    dispatch(streamStart(message));
    const cid = conversationId;

    return new Promise((resolve, reject) => {
      let fulfilled = false;
      let fullText = "";

      const fallback = async (errMsg, errCode, extra = {}) => {
        if (fulfilled) return;
        fulfilled = true;
        try {
          const { queryAi } = await import("../../services/ai/aiApi");
          const response = await queryAi(message, cid);
          dispatch(addMessage({
            id: (Date.now() + 1).toString(),
            role: "assistant",
            content: response.reply,
            timestamp: new Date().toISOString(),
          }));
          resolve({
            userMessage: message,
            reply: response.reply,
            conversationId: response.conversationId || cid,
            dailyUsed: extra.dailyUsed || response.dailyUsed,
            dailyMax: extra.dailyMax || response.dailyMax,
          });
        } catch {
          const payload = { message: errMsg, errorCode: errCode || "" };
          if (extra.dailyUsed != null) payload.dailyUsed = extra.dailyUsed;
          if (extra.dailyMax != null) payload.dailyMax = extra.dailyMax;
          dispatch(streamError(payload));
          reject(rejectWithValue(payload));
        }
      };

      queryAiStream(
        message,
        cid,
        (token) => {
          fullText += token;
          dispatch(streamToken(token));
        },
        (newConversationId, aiPrefs, dailyUsed, dailyMax) => {
          if (fulfilled) return;
          fulfilled = true;
          dispatch(streamEnd({
            conversationId: newConversationId || cid,
            dailyUsed,
            dailyMax,
          }));
          resolve({
            userMessage: message,
            reply: fullText,
            conversationId: newConversationId || cid,
            dailyUsed,
            dailyMax,
          });
        },
        (errorMessage, errorCode, extra = {}) => {
          fallback(errorMessage, errorCode, extra);
        },
      );

      setTimeout(() => {
        if (!fulfilled) {
          fallback("Request timed out. The server did not respond in time.");
        }
      }, 55000);
    });
  },
);

export const fetchAiPreferences = createAsyncThunk(
  "ai/fetchPreferences",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAiPreferences();
      return response;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch preferences");
    }
  },
);

export const saveAiPreferences = createAsyncThunk(
  "ai/savePreferences",
  async (preferences, { rejectWithValue }) => {
    try {
      const response = await updateAiPreferences(preferences);
      return response;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to save preferences");
    }
  },
);

export const fetchAiStatus = createAsyncThunk(
  "ai/fetchStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await checkAiStatus();
      return response;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to check AI status");
    }
  },
);

export const fetchAiUsage = createAsyncThunk(
  "ai/fetchUsage",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAiDailyUsage();
      return response;
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch AI usage");
    }
  },
);

const initialState = {
  conversations: [],
  loading: false,
  error: null,
  rateLimitRetryAfter: 0,
  isOpen: false,
  conversationId: null,
  preferences: {
    tone: "professional",
    speechLanguage: "en-IN",
    assistantVoice: "female",
  },
  preferencesLoading: false,
  preferencesError: null,
  streamingContent: "",
  messagesToSave: null,
  isAiDisabled: false,
  dailyUsed: 0,
  dailyMax: 1000,
};

const aiSlice = createSlice({
  name: "ai",
  initialState,
  reducers: {
    setOpen(state, action) {
      state.isOpen = action.payload;
      if (!action.payload) {
        state.error = null;
        state.rateLimitRetryAfter = 0;
        state.streamingContent = "";
      }
    },
    toggleOpen(state) {
      state.isOpen = !state.isOpen;
      if (state.isOpen) {
        state.error = null;
        state.rateLimitRetryAfter = 0;
      } else {
        state.streamingContent = "";
      }
    },
    addMessage(state, action) {
      state.conversations.push(action.payload);
    },
    clearConversation(state) {
      state.conversations = [];
      state.conversationId = null;
      state.error = null;
      state.rateLimitRetryAfter = 0;
      state.streamingContent = "";
    },
    clearError(state) {
      state.error = null;
      state.rateLimitRetryAfter = 0;
    },
    restoreConversation(state, action) {
      state.conversations = action.payload.messages || [];
      state.conversationId = action.payload.id || null;
    },
    setPreferences(state, action) {
      state.preferences = { ...state.preferences, ...action.payload };
    },
    streamStart(state, action) {
      state.loading = true;
      state.error = null;
      state.streamingContent = "";
      // Don't increment dailyUsed here — only count successful AI assistant responses (backend increments after reply)
      state.conversations.push({
        id: Date.now().toString(),
        role: "user",
        content: action.payload,
        timestamp: new Date().toISOString(),
      });
    },
    streamToken(state, action) {
      state.streamingContent += action.payload;
    },
    streamEnd(state, action) {
      state.loading = false;
      state.conversationId = action.payload.conversationId;
      state.conversations.push({
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: state.streamingContent,
        timestamp: new Date().toISOString(),
      });
      state.streamingContent = "";
      if (action.payload.dailyUsed != null) state.dailyUsed = action.payload.dailyUsed;
      if (action.payload.dailyMax != null) state.dailyMax = action.payload.dailyMax;
    },
    streamError(state, action) {
      state.loading = false;
      state.streamingContent = "";
      state.error = action.payload.message || "Something went wrong. Please try again.";
      if (action.payload.errorCode === "RATE_LIMIT_EXCEEDED") {
        state.rateLimitRetryAfter = 30;
      }
      if (action.payload.errorCode === "DAILY_LIMIT_EXCEEDED") {
        state.rateLimitRetryAfter = 0;
        if (action.payload.dailyUsed != null) state.dailyUsed = action.payload.dailyUsed;
        if (action.payload.dailyMax != null) state.dailyMax = action.payload.dailyMax;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Don't increment dailyUsed on user request — only count successful AI assistant responses (backend increments after reply)
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.conversationId = action.payload.conversationId;

        state.conversations.push({
          id: Date.now().toString(),
          role: "user",
          content: action.payload.userMessage,
          timestamp: new Date().toISOString(),
        });

        state.conversations.push({
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: action.payload.reply,
          timestamp: new Date().toISOString(),
        });

        if (action.payload.dailyUsed != null) state.dailyUsed = action.payload.dailyUsed;
        if (action.payload.dailyMax != null) state.dailyMax = action.payload.dailyMax;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        state.error = payload.message || "Something went wrong. Please try again.";
        if (payload.errorCode === "DAILY_LIMIT_EXCEEDED") {
          state.rateLimitRetryAfter = 0;
          if (payload.dailyUsed != null) state.dailyUsed = payload.dailyUsed;
          if (payload.dailyMax != null) state.dailyMax = payload.dailyMax;
        } else {
          state.rateLimitRetryAfter = payload.retryAfter || 0;
        }
      })
      .addCase(sendMessageStream.pending, (state) => {
        state.loading = true;
      })
      .addCase(sendMessageStream.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendMessageStream.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        state.error = payload.message || "Something went wrong. Please try again.";
        if (payload.errorCode === "DAILY_LIMIT_EXCEEDED") {
          state.rateLimitRetryAfter = 0;
          if (payload.dailyUsed != null) state.dailyUsed = payload.dailyUsed;
          if (payload.dailyMax != null) state.dailyMax = payload.dailyMax;
        } else {
          state.rateLimitRetryAfter = payload.retryAfter || 0;
        }
      })
      .addCase(fetchAiPreferences.pending, (state) => {
        state.preferencesLoading = true;
        state.preferencesError = null;
      })
      .addCase(fetchAiPreferences.fulfilled, (state, action) => {
        state.preferencesLoading = false;
        state.preferences = { ...state.preferences, ...action.payload };
      })
      .addCase(fetchAiPreferences.rejected, (state, action) => {
        state.preferencesLoading = false;
        state.preferencesError = action.payload;
      })
      .addCase(saveAiPreferences.pending, (state) => {
        state.preferencesLoading = true;
        state.preferencesError = null;
      })
      .addCase(saveAiPreferences.fulfilled, (state, action) => {
        state.preferencesLoading = false;
        state.preferences = { ...state.preferences, ...action.payload };
      })
      .addCase(saveAiPreferences.rejected, (state, action) => {
        state.preferencesLoading = false;
        state.preferencesError = action.payload;
      })
      .addCase(fetchAiStatus.fulfilled, (state, action) => {
        state.isAiDisabled = !action.payload.enabled;
      })
      .addCase(fetchAiStatus.rejected, (state) => {
        state.isAiDisabled = false;
      })
      .addCase(fetchAiUsage.fulfilled, (state, action) => {
        state.dailyUsed = action.payload.used;
        state.dailyMax = action.payload.max;
      })
      .addCase(fetchAiUsage.rejected, (state) => {
        state.dailyUsed = 0;
        state.dailyMax = 1000;
      });
  },
});

export const {
  setOpen,
  toggleOpen,
  addMessage,
  clearConversation,
  clearError,
  restoreConversation,
  setPreferences,
  streamStart,
  streamToken,
  streamEnd,
  streamError,
} = aiSlice.actions;

export default aiSlice.reducer;
